<script setup>
import { computed, ref } from 'vue';
import { fromUnixTime, isToday, isYesterday, isSameYear, format } from 'date-fns';
import { es } from 'date-fns/locale';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isATiktokChannel,
} = useInbox();

const {
  status,
  isPrivate,
  createdAt,
  sourceId,
  messageType,
  contentAttributes,
} = useMessageContext();

// Mensaje editado (Victor 2026-06-06): sello "✏️ Editado · ver original" en la
// línea de la hora. El original se conserva en content_attributes.originalContent.
const isEdited = computed(() => !!contentAttributes.value?.edited);
const originalContent = computed(() => contentAttributes.value?.originalContent);
const showOriginal = ref(false);

const readableTime = computed(() => {
  const ts = createdAt.value;
  if (!ts) return '';
  const date = fromUnixTime(ts);
  const time = format(date, 'h:mm a');
  if (isToday(date)) return `Hoy ${time}`;
  if (isYesterday(date)) return `Ayer ${time}`;
  if (isSameYear(date, new Date())) return `${format(date, "d 'de' MMMM", { locale: es })} ${time}`;
  return `${format(date, "d 'de' MMM yyyy", { locale: es })} ${time}`;
});

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // API inbox: usar el status real (nuestro bridge WhatsApp emite los 3 estados).
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.SENT;

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  // API inbox: usar el status real (delivered = ✓✓ gris).
  if (isAPIInbox.value) return status.value === MESSAGE_STATUS.DELIVERED;
  // Web widget inbox: una vez enviado se considera entregado.
  if (isAWebWidgetInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  // Fallback robusto (Victor 2026-06-03): para CUALQUIER mensaje saliente,
  // mostrar las palomitas según el status REAL del mensaje, aunque no se haya
  // podido detectar el canal (channelType undefined en vista compartida / inbox
  // no cargado en el store del agente). Antes esto solo aplicaba a API/WhatsApp/
  // Instagram y caía a PROGRESS (reloj) en PC, mientras en celular —donde el
  // inbox sí estaba cargado— se veían bien. Ahora se ven igual en PC y celular:
  //   read → ✓✓ azul · delivered → ✓✓ gris · sent (o sin ACK) → ✓ gris.
  if (messageType.value === MESSAGE_TYPES.OUTGOING) {
    if (status.value === MESSAGE_STATUS.READ) return MESSAGE_STATUS.READ;
    if (status.value === MESSAGE_STATUS.DELIVERED) return MESSAGE_STATUS.DELIVERED;
    return MESSAGE_STATUS.SENT;
  }

  return MESSAGE_STATUS.PROGRESS;
});
</script>

<template>
  <div
    v-if="!isEdited"
    class="text-[10px] leading-none flex items-center gap-1 opacity-80"
  >
    <time>{{ readableTime }}</time>
    <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
  </div>
  <!-- Sello de mensaje editado (Victor 2026-06-06): lápiz dorado + "Editado" +
       "ver original" en la misma línea de la hora; al tocar despliega el texto
       anterior tachado. -->
  <div v-else class="flex flex-col gap-1 items-end">
    <div
      class="text-[10px] leading-none flex items-center gap-1 opacity-80 flex-wrap justify-end"
    >
      <Icon icon="i-lucide-pencil" class="size-2.5 text-[#c9a35a]" />
      <span class="italic text-n-slate-11">Editado</span>
      <button
        v-if="originalContent"
        type="button"
        class="skip-context-menu text-[#c9a35a] underline decoration-dotted underline-offset-2"
        @click.stop="showOriginal = !showOriginal"
      >
        {{ showOriginal ? 'ocultar original' : 'ver original' }}
      </button>
      <span class="text-n-slate-11">·</span>
      <time>{{ readableTime }}</time>
      <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
      <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
    </div>
    <div
      v-if="showOriginal && originalContent"
      class="text-[11px] text-n-slate-11 line-through opacity-70 text-right border-r-2 border-n-slate-5 pr-2"
    >
      {{ originalContent }}
    </div>
  </div>
</template>
`
